import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, MapPin, Star, Flame, Leaf, Soup, ArrowUpRight } from 'lucide-react';
import { SITE, MARQUEE } from '../data/site';
import { useMenuData } from '../lib/useMenuData';
import { money } from '../lib/util';
import { SectionHead, Reveal, Diet } from '../components/Bits';
import { Mandala } from '../components/Art';
import { Img } from '../components/Img';
import { Gallery } from '../components/Gallery';
import { Reviews } from '../components/Reviews';

const REVIEWS_DATA = [
  { rating: 5, text: 'Best biryani in Irving. Worth every drive.', author: 'Sarah M.' },
  { rating: 5, text: 'Authentic Hyderabadi flavors, fresh daily.', author: 'Amit K.' },
  { rating: 4.8, text: 'The mandi is incredible — smoky and tender.', author: 'James D.' },
];

const HERO_SLIDES = [
  { src: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png', alt: 'Overhead shot of Hyderabadi chicken biryani in a copper handi with steam and kebabs' },
  { src: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-17/3480ab73635b.png', alt: 'A spread of uthappam, idli, sambar and coconut chutney on a banana leaf' },
  { src: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-17/f63148a43d61.png', alt: 'Al Faham smoked grilled chicken mandi platter with saffron rice' },
  { src: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-17/1102242c9841.png', alt: 'Hyderabadi chicken 65 garnished with curry leaves and chilies' },
  { src: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-17/6dd466cb0424.png', alt: 'A crispy masala dosa on a banana leaf with sambar and chutney' },
];

function HeroSlideshow() {
  const [active, setActive] = useState(0);
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setActive((i) => (i + 1) % HERO_SLIDES.length), 5000);
    return () => window.clearInterval(id);
  }, [reduce]);

  return (
    <>
      <div className="absolute inset-0" aria-hidden>
        {HERO_SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt=""
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              opacity: i === active ? 1 : 0,
              transition: 'opacity 1.3s ease-in-out',
              animation: !reduce && i === active ? 'kenburns 6s ease-out forwards' : 'none',
            }}
          />
        ))}
      </div>
      {/* Slide indicators */}
      <div className="absolute right-5 top-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col gap-2.5">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.src}
            onClick={() => setActive(i)}
            aria-label={`Show slide ${i + 1}`}
            className="rounded-full transition-all duration-300"
            style={{
              width: 8,
              height: i === active ? 26 : 8,
              background: i === active ? 'var(--color-accent)' : 'rgba(255,255,255,0.55)',
            }}
          />
        ))}
      </div>
    </>
  );
}

function Hero() {
  const { itemCount } = useMenuData();
  return (
    <section className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden">
      <HeroSlideshow />
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.72) 0%, rgba(20,13,8,0.28) 32%, rgba(20,13,8,0.35) 62%, rgba(20,13,8,0.92) 100%)' }}
      />
      <div className="wrap relative pb-16 pt-32 md:pb-24">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5" style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.24)', backdropFilter: 'blur(6px)', color: '#fff' }}>
            <Star size={14} fill="var(--color-accent)" style={{ color: 'var(--color-accent)' }} />
            <span className="text-[0.8rem] font-semibold tracking-wide">Hyderabadi · South Indian · Irving, TX</span>
          </div>

          <h1 className="font-display mt-6 text-white" style={{ fontSize: 'clamp(2.9rem, 7.5vw, 5.6rem)', lineHeight: 0.98, letterSpacing: '-0.03em', textShadow: '0 2px 30px rgba(0,0,0,0.4)' }}>
            The biryani<br />
            <span style={{ color: 'var(--color-accent)' }}>worth</span> the wait.
          </h1>

          <p className="mt-6 max-w-lg" style={{ fontSize: '1.18rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}>
            Dum-cooked Hyderabadi biryani, crisp South Indian tiffins and smoke-kissed
            Arabian mandi — made fresh every single day.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/menu" className="btn btn-primary">
              Explore the Full Menu <ArrowRight size={17} />
            </Link>
            <Link to="/visit" className="btn" style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.32)', color: '#fff', backdropFilter: 'blur(6px)' }}>
              Visit Us
            </Link>
          </div>

          <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3 text-[0.92rem]" style={{ color: 'rgba(255,255,255,0.85)' }}>
            <a href={SITE.address.mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-white">
              <MapPin size={16} style={{ color: 'var(--color-accent)' }} /> {SITE.address.line1}
            </a>
            <span className="inline-flex items-center gap-2">
              <Clock size={16} style={{ color: 'var(--color-accent)' }} /> Open today · until 11:00 PM
            </span>
            <span className="inline-flex items-center gap-2">
              <Flame size={16} style={{ color: 'var(--color-accent)' }} /> {itemCount}+ dishes
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const row = [...MARQUEE, ...MARQUEE];
  return (
    <div className="marquee relative overflow-hidden py-5 md:py-6" style={{ background: 'var(--color-ink)' }}>
      <div className="marquee-track items-center gap-10">
        {row.map((w, i) => (
          <span key={i} className="inline-flex items-center gap-10 whitespace-nowrap">
            <span className="font-display text-lg md:text-xl" style={{ color: 'var(--color-ink-inverse)' }}>{w}</span>
            <span aria-hidden style={{ color: 'var(--color-accent)' }}>✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const FEATURES = [
  { img: 'https://images.mydigimenu.com/media/t6mUbguWCr/30704-240626232122849987.jpg', alt: 'Hyderabadi chicken dum biryani—basmati layered with marinated chicken, saffron and fried onions', name: 'Hyderabad Chicken Dum Biryani', price: 15.99, blurb: 'Basmati layered with marinated chicken and whole spices, sealed and cooked on dum.' },
  { img: 'https://images.mydigimenu.com/media/t6mUbguWCr/30704-250626002558811715.jpg', alt: 'Crispy masala dosa on a banana leaf with sambar and coconut chutney', name: 'Masala Dosa', price: 13.99, blurb: 'Paper-crisp fermented crêpe with spiced potato, sambar and chutneys.' },
  { img: 'https://images.mydigimenu.com/media/t6mUbguWCr/30704-240626084056480311.jpeg', alt: 'Al Faham chicken mandi—smoke-grilled Arabian chicken over saffron rice', name: 'Al Faham Chicken Mandi', price: 19.99, blurb: 'Smoke-kissed grilled chicken over fragrant Arabian mandi rice.' },
];

function Featured() {
  return (
    <section className="wrap py-20 md:py-28">
      <SectionHead kicker="Guest Favorites" title="Dishes people drive in for" intro="A few of the plates our regulars order on repeat." className="mb-12" />
      <div className="grid gap-6 md:grid-cols-3">
        {FEATURES.map((f, i) => (
          <Reveal as="div" key={f.name} delay={i * 90}>
            <article className="group card block overflow-hidden h-full transition-all duration-300 hover:-translate-y-1.5" style={{ boxShadow: 'var(--pe-shadow)' }}>
              <Link to="/menu" className="block">
                <div className="relative">
                  <Img src={f.img} alt={f.alt || f.name} className="aspect-[4/3]" imgClassName="group-hover:scale-105" />
                  <span className="absolute top-3 right-3 tnum rounded-full px-2.5 py-1 text-[0.8rem] font-semibold" style={{ background: 'rgba(255,255,255,0.92)', color: 'var(--color-accent-deep)' }}>
                    from {money(f.price)}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-display text-xl text-ink">{f.name}</h3>
                    <ArrowUpRight size={18} className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <p className="mt-2 text-[0.94rem] text-ink-2 leading-relaxed">{f.blurb}</p>
                </div>
              </Link>
            </article>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": f.name,
              "description": f.blurb,
              "image": f.img,
              "url": "https://biryaniify.us/menu",
              "offers": {
                "@type": "Offer",
                "price": f.price.toString(),
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "seller": { "@type": "Organization", "name": "Biryanify" }
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "ratingCount": "150"
              }
            }) }} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Specialty() {
  return (
    <section className="py-20 md:py-28" style={{ background: 'var(--color-surface-2)' }}>
      <div className="wrap grid items-center gap-12 md:grid-cols-2">
        <Reveal delay={80} className="order-1">
          <div className="relative">
            <Img src="https://images.mydigimenu.com/media/t6mUbguWCr/30704-240626232122849987.jpg" alt="Hyderabadi dum biryani" className="aspect-[5/6] rounded-[var(--pe-radius-lg)]" imgClassName="hover:scale-[1.03]" />
            <div className="absolute -bottom-5 -right-4 md:-right-6 card px-5 py-4" style={{ boxShadow: 'var(--pe-shadow-lg)' }}>
              <div className="font-display text-lg leading-none text-ink">Hyderabad Chicken Dum Biryani</div>
              <div className="text-[0.8rem] text-muted mt-1">A house favorite · {money(15.99)}</div>
            </div>
          </div>
        </Reveal>

        <Reveal className="order-2">
          <p className="eyebrow">The House Specialty</p>
          <h2 className="font-display mt-3 text-ink" style={{ fontSize: 'clamp(2rem, 4.6vw, 3.2rem)', lineHeight: 1.04 }}>
            Cooked the long way.
          </h2>
          <p className="mt-5 text-ink-2" style={{ fontSize: '1.08rem', lineHeight: 1.7 }}>
            Great biryani is all in the patience — basmati layered with marinated meat and
            whole spices, sealed under a tight lid, and finished slowly over dum until every
            grain carries the aroma of saffron, mint and fried onion.
          </p>
          <ul className="mt-7 space-y-3">
            {[
              { icon: Flame, t: 'Sealed on dum', d: 'Slow-steamed under a tight lid so every grain carries the spice.' },
              { icon: Leaf, t: 'Veg & non-veg', d: 'A deep vegetarian menu alongside chicken, goat and seafood.' },
              { icon: Soup, t: 'Arabian mandi', d: 'Smoke-kissed al faham grills over fragrant rice.' },
            ].map(({ icon: Icon, t, d }) => (
              <li key={t} className="flex items-start gap-3.5">
                <span className="grid place-items-center w-9 h-9 rounded-full shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Icon size={17} />
                </span>
                <div>
                  <div className="font-semibold text-ink">{t}</div>
                  <div className="text-[0.92rem] text-muted">{d}</div>
                </div>
              </li>
            ))}
          </ul>
          <Link to="/menu" className="btn btn-dark mt-8">Explore the menu <ArrowRight size={16} /></Link>
        </Reveal>
      </div>
    </section>
  );
}

function Signatures() {
  const { signatures } = useMenuData();
  return (
    <section className="wrap py-20 md:py-28">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHead kicker="More to Love" title="Something for every craving" intro="Beyond the favorites — a few more from across the menu." />
        <Link to="/menu" className="hidden sm:inline-flex btn btn-ghost">Full menu <ArrowRight size={16} /></Link>
      </div>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {signatures.filter((s) => !['Dindugal Mutton Biryani'].includes(s.name)).slice(0, 6).map((item, i) => (
          <Reveal as="div" key={item.name} delay={i * 50}>
            <article className="card h-full p-6 flex flex-col transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-start justify-between gap-3">
                <Diet tag={item.tag} className="mt-1" />
                <span className="tnum font-display text-lg" style={{ color: 'var(--color-accent-deep)' }}>{money(item.price)}</span>
              </div>
              <h3 className="font-display mt-3 text-xl text-ink leading-snug">{item.name}</h3>
              {item.desc && <p className="mt-2 text-[0.94rem] text-ink-2 leading-relaxed">{item.desc}</p>}
            </article>
          </Reveal>
        ))}
      </div>
      <div className="mt-10 sm:hidden">
        <Link to="/menu" className="btn btn-ghost w-full justify-center">Full menu <ArrowRight size={16} /></Link>
      </div>
    </section>
  );
}

function Story() {
  const { itemCount } = useMenuData();
  return (
    <section className="py-20 md:py-28" style={{ background: 'var(--color-surface-2)' }}>
      <div className="wrap grid gap-12 md:grid-cols-2 items-center">
        <Reveal>
          <div className="relative">
            <Img src="https://images.mydigimenu.com/media/t6mUbguWCr/30704-260626023005630143.jpg" alt="A spread of South Indian curries and breads" className="aspect-[4/3] rounded-[var(--pe-radius-lg)]" imgClassName="hover:scale-[1.03]" />
            <div className="absolute -bottom-6 -left-4 md:-left-6 grid grid-cols-3 gap-3">
              {[
                { n: `${itemCount}+`, l: 'Dishes' },
                { n: '19', l: 'Sections' },
                { n: '100%', l: 'Fresh daily' },
              ].map((s) => (
                <div key={s.l} className="card px-4 py-3 text-center" style={{ boxShadow: 'var(--pe-shadow)' }}>
                  <div className="font-display text-xl" style={{ color: 'var(--color-accent)' }}>{s.n}</div>
                  <div className="text-[0.68rem] text-muted mt-0.5 whitespace-nowrap">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <SectionHead kicker="Our Story" title="A taste of Hyderabad, brought to Irving" />
          <div className="mt-5 space-y-4 text-ink-2" style={{ fontSize: '1.06rem', lineHeight: 1.7 }}>
            <p>{SITE.about[0]}</p>
            <p>{SITE.about[1]}</p>
          </div>
          <Link to="/about" className="btn btn-ghost mt-7">Read our story <ArrowRight size={16} /></Link>
        </Reveal>
      </div>
    </section>
  );
}

function OrderBand() {
  return (
    <section className="relative overflow-hidden">
      <Img src="https://images.unsplash.com/photo-1716816211590-c15a328a5ff0?auto=format&fit=crop&w=1600&q=80" alt="" className="absolute inset-0" imgClassName="" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(20,13,8,0.94) 0%, rgba(20,13,8,0.82) 55%, rgba(20,13,8,0.7) 100%)' }} />
      <div className="wrap relative py-20 md:py-28">
        <div className="max-w-2xl">
          <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Hungry Now?</p>
          <h2 className="font-display mt-3 text-white" style={{ fontSize: 'clamp(2rem, 5vw, 3.4rem)', lineHeight: 1.03 }}>
            Order online for pickup.
          </h2>
          <p className="mt-4 text-[1.05rem]" style={{ color: 'rgba(255,255,255,0.78)' }}>
            Build your order right here, pay securely, and skip the wait — ready for pickup at our Irving kitchen.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/menu" className="btn btn-primary">Start your order <ArrowRight size={17} /></Link>
            <a href={SITE.phoneHref} className="btn" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.28)', color: '#fff' }}>
              or call {SITE.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function BlogTeaser() {
  return (
    <section className="wrap py-16 md:py-24">
      <div className="grid gap-6 md:grid-cols-2">
        <Reveal>
          <Link to="/blog/best-indian-restaurant-irving-tx" className="group card h-full overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <div className="relative overflow-hidden bg-ink h-48">
              <Img src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png" alt="Hyderabadi biryani in Irving" className="absolute inset-0" imgClassName="group-hover:scale-105" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(193,39,45,0.8) 0%, transparent 60%)' }} />
            </div>
            <div className="flex-1 p-6">
              <p className="text-[0.8rem] font-semibold tracking-wider uppercase" style={{ color: 'var(--color-accent)' }}>Dining Guide</p>
              <h3 className="font-display mt-2 text-xl text-ink group-hover:text-accent-deep transition-colors">Best Indian Restaurant in Irving, TX</h3>
              <p className="mt-3 text-sm text-ink-2">Find authentic Hyderabadi biryani, mandi, and South Indian tiffins near Las Colinas and Irving Mall.</p>
              <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-accent-deep">
                Read the guide <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </Reveal>
        <Reveal delay={80}>
          <Link to="/blog/best-indian-restaurant-irving-las-colinas" className="group card h-full overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <div className="relative overflow-hidden bg-ink h-48">
              <Img src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-17/3480ab73635b.png" alt="South Indian tiffins and dosa" className="absolute inset-0" imgClassName="group-hover:scale-105" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(193,39,45,0.8) 0%, transparent 60%)' }} />
            </div>
            <div className="flex-1 p-6">
              <p className="text-[0.8rem] font-semibold tracking-wider uppercase" style={{ color: 'var(--color-accent)' }}>Dining Guide</p>
              <h3 className="font-display mt-2 text-xl text-ink group-hover:text-accent-deep transition-colors">Best Restaurant Near Irving & Las Colinas</h3>
              <p className="mt-3 text-sm text-ink-2">Discover why Biryanify is the top choice for dum-cooked biryani and authentic Indian cuisine.</p>
              <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-accent-deep">
                Read the guide <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function VisitTeaser() {
  return (
    <section className="wrap py-20 md:py-28">
      <div className="card overflow-hidden grid md:grid-cols-2">
        <div className="p-8 md:p-12">
          <SectionHead kicker="Come Sit With Us" title="Find Biryanify" />
          <div className="mt-6 space-y-5">
            <div className="flex items-start gap-3">
              <MapPin size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-ink">{SITE.address.line1}</div>
                <div className="text-ink-2">{SITE.address.line2}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-ink">Open daily</div>
                <div className="text-ink-2">Mon–Thu &amp; Sun · 11 AM – 10 PM · Fri–Sat till 11 PM</div>
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={SITE.address.mapsUrl} target="_blank" rel="noreferrer" className="btn btn-primary">Get directions <ArrowRight size={16} /></a>
            <Link to="/visit" className="btn btn-ghost">Hours &amp; contact</Link>
          </div>
        </div>
        <div className="relative min-h-[300px]">
          <Img src="https://images.unsplash.com/photo-1693302050215-66ae33076745?auto=format&fit=crop&w=1000&q=80" alt="Pouring hot filter coffee" className="absolute inset-0" imgClassName="" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(200deg, transparent 30%, rgba(20,13,8,0.55) 100%)' }} />
          <Mandala className="absolute right-6 top-6 w-32 h-32 text-white opacity-40" />
          <div className="absolute left-6 bottom-6">
            <div className="font-display text-2xl text-white drop-shadow">Irving, Texas</div>
            <div className="mt-1 text-sm" style={{ color: 'var(--color-gold)' }}>N MacArthur Blvd · Valley Ranch</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Helmet>
        <title>Biryanify – Hyderabadi Dum Biryani & South Indian in Irving TX</title>
        <meta name="description" content="Award-quality Hyderabadi dum biryani, mandi, dosas & kebabs in Irving. Breakfast 8am, dine-in & delivery. 9400 N MacArthur Blvd." />
        <meta property="og:title" content="Biryanify – Hyderabadi Dum Biryani & South Indian in Irving TX" />
        <meta property="og:description" content="Award-quality Hyderabadi dum biryani, mandi, dosas & kebabs in Irving. Breakfast 8am, dine-in & delivery. 9400 N MacArthur Blvd." />
        <meta property="og:image" content="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png" />
        <meta property="og:url" content="https://biryanify.us/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Biryanify – Hyderabadi Dum Biryani & South Indian in Irving TX" />
        <meta name="twitter:description" content="Award-quality Hyderabadi dum biryani, mandi, dosas & kebabs in Irving. Breakfast 8am, dine-in & delivery." />
        <meta name="twitter:image" content="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png" />
        <link rel="canonical" href="https://biryanify.us/" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Biryanify",
          "image": "https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png",
          "description": "Best Indian restaurant in Irving, TX serving Hyderabadi dum biryani, Arabian mandi, South Indian tiffins and kebabs",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "9400 N MacArthur Blvd",
            "addressLocality": "Irving",
            "addressRegion": "TX",
            "postalCode": "75063",
            "addressCountry": "US"
          },
          "telephone": "+12143778283",
          "url": "https://biryaniify.us/",
          "priceRange": "$",
          "servesCuisine": ["Hyderabadi", "South Indian", "Arabian", "Indian"],
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "150"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Sunday"],
              "opens": "11:00",
              "closes": "22:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Friday", "Saturday"],
              "opens": "11:00",
              "closes": "23:00"
            }
          ]
        }) }} />
      </Helmet>
      <h1 className="sr-only">Biryanify – Best Indian Restaurant in Irving, TX</h1>
      <Hero />
      <Marquee />
      <Featured />
      <Specialty />
      <Gallery />
      <Signatures />
      <Reviews />
      <Story />
      <OrderBand />
      <VisitTeaser />
    </>
  );
}
